import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { TaskStatus, JourneyTaskProgressRow } from "@colab/shared";
import type { PortalJourneyDetail } from "./types";

export type SetProgressStatusInput = {
  progressId: string;
  // assignmentId is required so onMutate can patch the right detail key
  // without a server round-trip to look it up. Pages call this from the
  // journey-detail context where assignment_id is already in scope.
  assignmentId: string;
  status: TaskStatus;
};

type Context = {
  previousDetail: PortalJourneyDetail | undefined;
};

// PATCH /api/portal/journeys/progress/:progressId
//
// Replaces /app/portal/journeys/_actions.ts ::
//   markProgressCompleteAction / markProgressInProgressAction /
//   markProgressPendingAction
//
// Optimistic Pattern C — state flip. Three-key invalidation matrix per
// bucket-12 brief: patch portal.journeys.detail(assignmentId) on mutate
// (the row carrying the status field), then on settle invalidate three
// surfaces so every partner-facing view of this task refetches:
//   1. portal.journeys.detail(assignmentId) — journey-detail page
//   2. portal.tasks.list()                  — dashboard active-task feed
//   3. portal.tasks.openCount()             — top-nav badge
//
// Phase 7 doesn't yet render (2) and (3); the keys are pre-built so day-
// one consumers land correctly. Bucket-11 admin hooks broadcast against
// portal.journeys.all + portal.tasks.all for the same reason.
export function useSetProgressStatus() {
  const qc = useQueryClient();
  return useMutation<
    JourneyTaskProgressRow,
    ApiClientError,
    SetProgressStatusInput,
    Context
  >({
    mutationFn: ({ progressId, status }) =>
      apiFetch<JourneyTaskProgressRow>(
        `/portal/journeys/progress/${progressId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ status }),
        },
      ),
    onMutate: async ({ progressId, assignmentId, status }) => {
      const key = queryKeys.portal.journeys.detail(assignmentId);
      await qc.cancelQueries({ queryKey: key });
      const previousDetail = qc.getQueryData<PortalJourneyDetail>(key);
      qc.setQueryData<PortalJourneyDetail>(key, (old) =>
        old
          ? {
              ...old,
              progress: old.progress.map((p) =>
                p.id === progressId
                  ? {
                      ...p,
                      status,
                      completed_at:
                        status === "completed"
                          ? new Date().toISOString()
                          : null,
                    }
                  : p,
              ),
            }
          : old,
      );
      return { previousDetail };
    },
    onError: (err, { assignmentId }, ctx) => {
      if (ctx?.previousDetail !== undefined) {
        qc.setQueryData(
          queryKeys.portal.journeys.detail(assignmentId),
          ctx.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to update task");
    },
    onSettled: (_data, _err, { assignmentId }) => {
      qc.invalidateQueries({
        queryKey: queryKeys.portal.journeys.detail(assignmentId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.portal.tasks.list() });
      qc.invalidateQueries({ queryKey: queryKeys.portal.tasks.openCount() });
    },
  });
}
